/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as THREE from 'three';

import { COLLISION_LAYERS, LOCOMOTION_CONSTANTS } from '../../Constants';

import { CollisionWorldComponent } from '../../components/ColliderComponents';
import { GameStateComponent } from '../../components/GameStateComponent';
import { SharedWorldComponent } from '../../components/MultiplayerComponents';
import { StaticColliderComponent } from '../../components/ColliderComponents';
import { System } from 'ecsy';
import { THREEGlobalComponent } from '../../components/THREEGlobalComponent';
import { getOnlyEntity } from '../../utils/entityUtils';
import { gridGroundExtent } from '../../lib/world/plotGrid';

/**
 * Extends the single bounded island into a walkable grid (design Phase 1).
 *
 * The world is one finite island, so the shared-world plot grid has nowhere for
 * neighbouring plots to live. This system lays a large flat ground surface across
 * the whole grid extent and registers it in the collision world as a
 * teleportable / plantable surface, so every plot cell has honest ground to stand
 * and garden on. Scenic per-cell terrain (island scenery replicated per plot) is
 * layered on top of this foundation in a later phase.
 *
 * Entirely dormant unless the shared world is configured (Firebase present); in
 * single-player the game keeps its original single island untouched.
 */
export class PlotGroundSystem extends System {
	init() {
		this._built = false;
	}

	execute() {
		if (this._built) return;

		const swEntity = getOnlyEntity(this.queries.sharedWorld, false);
		const sharedWorld = swEntity
			? swEntity.getComponent(SharedWorldComponent)
			: null;
		// Only extend the world when the shared layer is live; single-player keeps
		// its original bounded island.
		if (!sharedWorld || !sharedWorld.configured) return;

		const gameManager = getOnlyEntity(this.queries.gameManager, false);
		if (!gameManager) return;
		const gameState = gameManager.getComponent(GameStateComponent);
		// Wait until the environment (and therefore the collision world) is ready.
		if (!gameState.allAssetsLoaded) return;

		const collisionEntity = getOnlyEntity(this.queries.collisionWorld, false);
		if (!collisionEntity) return;

		const scene = gameManager.getComponent(THREEGlobalComponent).scene;
		this._buildGround(scene, gameState);
		this._built = true;
	}

	_buildGround(scene, gameState) {
		const extent = gridGroundExtent();
		const width = extent.maxX - extent.minX;
		const depth = extent.maxZ - extent.minZ;
		const centerX = (extent.minX + extent.maxX) / 2;
		const centerZ = (extent.minZ + extent.maxZ) / 2;

		// Ground height: the map's canonical foot-start height, which sits on the
		// island's walkable ground. The scenic island renders on top of this plane.
		const startPos =
			LOCOMOTION_CONSTANTS.INITIAL_POSITION[gameState.currentBaseMapId];
		const groundY = startPos ? startPos.y : 0;

		const geometry = new THREE.PlaneGeometry(width, depth, 1, 1);
		geometry.rotateX(-Math.PI / 2); // lie flat on the XZ plane
		const material = new THREE.MeshStandardMaterial({
			color: 0x6bbf59, // cozy grass green until real terrain art is layered on
			roughness: 1.0,
			metalness: 0.0,
		});
		const ground = new THREE.Mesh(geometry, material);
		ground.position.set(centerX, groundY, centerZ);
		ground.receiveShadow = true;
		ground.name = 'PlotGridGround';
		ground.updateMatrixWorld(true);
		scene.add(ground);

		// Register as a static collider so it is walkable, teleportable and
		// plantable across the whole grid.
		const colliderEntity = this.world.createEntity();
		colliderEntity.addComponent(StaticColliderComponent, {
			mesh: ground,
			layers: [
				COLLISION_LAYERS.OBSTACLE,
				COLLISION_LAYERS.TELEPORT_SURFACE,
				COLLISION_LAYERS.PLANTABLE_SURFACE,
			],
		});
	}
}

PlotGroundSystem.queries = {
	sharedWorld: { components: [SharedWorldComponent] },
	gameManager: { components: [THREEGlobalComponent, GameStateComponent] },
	collisionWorld: { components: [CollisionWorldComponent] },
};
